import useUserAccount from './userAccountHook';
import useModal from './modalHook';
import {
  getAllIntermediaries,
  getIntermediariesByBroker,
  getIntermediariesByInsurer
} from '../api/requests';
import CreateBrokerIntermediary from '../views/shared/settings/Intermediaries/components/CreateIntermediary';
import { BrokerInsurerIntegration } from '../views/shared/settings/Intermediaries';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import SendMIDAccessRequestModal from '../views/shared/settings/Intermediaries/SendMIDAccessRequestModal';
import Intermediary from '../models/Intermidiary';
import { POLICY_ISSUANCE_MODES } from '../resources/enums';
import { CheckCircle, Globe, Mail, Monitor } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useRoute from './routesHook';
import { useSelector } from 'react-redux';
import { getPendingActions } from '../states/slices/globalSlice';
import { useEffect, useState } from 'react';

const useIntermediaries = () => {
  const { user } = useUserAccount();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const IntermediariesModal = ModalComponent;

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const [shouldCheckRedirect, setShouldCheckRedirect] = useState(false);

  const pendingActions = useSelector(getPendingActions) || [];

  const { data: intermediaries, isLoading: isIntermidiaryLoading } = useQuery({
    queryKey: ['fetchAllIntermediaries'],
    queryFn: fetchAllIntermediaries,
  });

 useEffect(() => {
  if (!shouldCheckRedirect) return;

  const insurerConfiguration = pendingActions?.find((msg: string) =>
    msg?.toLowerCase().includes('insurance company')
  );

  if (insurerConfiguration) {
    navigate(`${getDashboardBase()}/dashboard`);
    setShouldCheckRedirect(false);
  }
}, [pendingActions, shouldCheckRedirect]);

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchAllIntermediaries'] });
  };

  async function fetchAllIntermediaries() {
    try {
      const response: any = await (
        user.isBroker()
          ? getIntermediariesByBroker(Number(user.company?.id))
          : user.isInsurer()
          ? getIntermediariesByInsurer(Number(user.company?.id))
          : getAllIntermediaries()
      );

      if (response) {
        return response?.data?.content?.map(
          (intermediary: any) => new Intermediary(intermediary)
        );
      }
    } catch (error) {
      console.log(error);
    }
  }

  const handleCreateNewIntermediary = (
    isCreaterequest: boolean,
    intermediary?: BrokerInsurerIntegration,
    readOnly?: boolean
  ) => {

    updateModalStates({
      show: true,
      children: (
        <CreateBrokerIntermediary
          onClose={closeModal}
          onSuccess={() => {
            invalidateQueries();
            setShouldCheckRedirect(true);
          }}
          intermediary={intermediary}
          isCreateRequest={isCreaterequest}
          readOnly={readOnly}
        />
      ),
      size: 'xl',
      showTitleSection: true,
      title: readOnly
        ? 'Underwriter Details'
        : isCreaterequest
          ? 'Add New Underwriter'
          : 'Edit Underwriter Details',
    });
  };

  const sendMIDAccessRequestToUnderwriter = (
    intermediary?: BrokerInsurerIntegration
  ) => {
    updateModalStates({
      show: true,
      children: (
        <SendMIDAccessRequestModal
          onClose={closeModal}
          onSuccess={() => {
            invalidateQueries();
            closeModal();
          }}
          intermediary={intermediary}
        />
      ),
      size: 'md',
      showTitleSection: true,
      title: 'Send MID Access Request',
    });
  };

  const PolicyIssuanceModeBadge = (mode: string) => {
    const modeConfig = {
      [POLICY_ISSUANCE_MODES.INSURER_API]: {
        label: 'Insurer API',
        color:
          'text-blue-600 border-blue-200 bg-blue-50 dark:text-blue-400 dark:border-blue-800 dark:bg-blue-900/20',
        icon: Globe,
      },
      [POLICY_ISSUANCE_MODES.MID_LIVE]: {
        label: 'MID Live',
        color:
          'text-green-600 border-green-200 bg-green-50 dark:text-green-400 dark:border-green-800 dark:bg-green-900/20',
        icon: CheckCircle,
      },
      [POLICY_ISSUANCE_MODES.EMAIL]: {
        label: 'Email',
        color:
          'text-yellow-600 border-yellow-200 bg-yellow-50 dark:text-yellow-400 dark:border-yellow-800 dark:bg-yellow-900/20',
        icon: Mail,
      },
      [POLICY_ISSUANCE_MODES.OFFLINE]: {
        label: 'Offline',
        color:
          'text-gray-600 border-gray-200 bg-gray-50 dark:text-gray-400 dark:border-gray-700 dark:bg-gray-800/20',
        icon: Monitor,
      },
    };

    const config = modeConfig[mode as POLICY_ISSUANCE_MODES];

    if (!config) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-600 border border-gray-200 bg-gray-50 dark:text-gray-400 dark:border-gray-700 dark:bg-gray-800/20">
          <Monitor className="w-3 h-3 mr-1" />
          Unknown
        </span>
      );
    }

    const { color, icon: Icon } = config;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  return {
    fetchAllIntermediaries,
    handleCreateNewIntermediary,
    sendMIDAccessRequestToUnderwriter,
    PolicyIssuanceModeBadge,
    IntermediariesModal,
    intermediaries,
    isIntermidiaryLoading,
  };
};

export default useIntermediaries;